// @flow

import _ from 'lodash';

import {
  HOME_FIELD_MAP,
  PROFILED_ADDRESS_FIELD_MAP,
  type FieldMapType,
} from 'routes/marketplace-ede/constants/address';
import { type AddressFieldsType as EdeAddressType } from 'routes/marketplace-ede/types/ede/edeMember';
import { EdeMemberType } from 'routes/marketplace-ede/types/ede/edeMember';
import { getPrimaryMember } from './edeMember';

type ProfileAddressType = {
  streetAddress: string,
  city: string,
  state: string,
  zipcode: string,
  county: string,
  countyFips: string,
};

export const ADDRESS_TYPE = {
  HOME: 'HOME',
  TRANSIENT: 'TRANSIENT',
  MAILING: 'MAILING',
};

export const ADDRESS_TYPE_PRIORITY = [ADDRESS_TYPE.TRANSIENT, ADDRESS_TYPE.HOME, ADDRESS_TYPE.MAILING];

const ATTESTED_ADDRESS_MAP = {
  street: 'streetName1',
  unit: 'streetName2',
  city: 'cityName',
  state: 'stateCode',
  zipcode: 'zipCode',
  county: 'countyName',
  countyFips: 'countyFipsCode',
};

const PROFILE_ADDRESS_MAP = {
  street: 'streetAddress',
  unit: 'apartmentSuite',
  city: 'city',
  state: 'state',
  zipcode: 'zipcode',
  county: 'county',
  countyFips: 'countyFips',
  region: 'region', // only profile will save region; edePayload doesn't have this field
};

type MapTypes = 'attestation' | 'profile';

export const mapAddressToFields = (
  address: EdeAddressType | ProfileAddressType | null,
  type: MapTypes = 'profile',
  fieldMap: FieldMapType = HOME_FIELD_MAP,
) =>
  Object.keys(fieldMap).reduce((acc, key) => {
    const MAPS = type === 'profile' ? PROFILE_ADDRESS_MAP : ATTESTED_ADDRESS_MAP;
    const val = _.get(address, MAPS[key]);
    return fieldMap[key] && val
      ? {
        ...acc,
        [fieldMap[key]]: key === 'county' ? val.toUpperCase() : val,
      }
      : acc;
  }, {});

export const mapFieldsToAddress = (fieldValues: FieldMapType, type: MapTypes, fieldMap: FieldMapType) =>
  Object.keys(fieldMap).reduce((acc, key) => {
    const MAPS = type === 'profile' ? PROFILE_ADDRESS_MAP : ATTESTED_ADDRESS_MAP;
    let val = _.get(fieldValues, fieldMap[key]);
    if (_.isUndefined(val)) {
      return acc;
    }
    // Clean commas and trim whitespace
    if (val !== null && (key === 'street' || key === 'unit')) {
      val = val.replace(/,/g, '').trim();
    }

    // Set empty unit field as null instead of empty string
    if (val !== null && key === 'unit') {
      val = val.length !== 0 ? val : null;
    }

    return MAPS[key] && val
      ? {
        ...acc,
        [MAPS[key]]: val,
      }
      : acc;
  }, {});

export const checkOutOfState = (address: EdeAddressType | ProfileAddressType, coverageState: string) => {
  const previousState = _.get(address, 'stateCode', _.get(address, 'state'));
  return previousState !== coverageState;
};

type GeoDataType = {
  fips: string,
  name: string,
  region: number,
  state: string,
  region: number,
};
type GeoResponseType = {
  zipcode: {
    city: string,
    counties: Array<GeoDataType>,
    zipcode: string,
  },
};

export type GeoMatchType = {
  city: string,
  state: string,
  zipcode: string,
  county: string,
  countyFips: string,
};

export const findGeoMatch = (
  address: FieldMapType,
  geoData: GeoResponseType,
  countySelected: boolean,
): {
  isMatch: boolean,
  data: GeoMatchType,
  counties: Array<Object>,
} => {
  const { county, city, state } = address;
  const {
    zipcode: { counties: geoCountyArray, city: geoCity, zipcode: geoZipcode },
  } = geoData;

  const matchedCounty =
    _.find(
      geoCountyArray,
      geo => _.isString(geo.name) && county && geo.name.toLowerCase() === county.toLowerCase(),
    ) || '';

  // fallback county match to first in the array
  const countyName =
    (matchedCounty && matchedCounty.name) || (geoCountyArray.length > 1 ? '' : geoCountyArray[0].name);
  const stateName = (matchedCounty && matchedCounty.state) || geoCountyArray[0].state;
  const countyFips = (matchedCounty && matchedCounty.fips) || geoCountyArray[0].fips;
  const region = (matchedCounty && matchedCounty.region) || geoCountyArray[0].region;

  let isMatch =
    state?.toLowerCase() === stateName?.toLowerCase() && city?.toLowerCase() === geoCity?.toLowerCase();

  if (isMatch && geoCountyArray.length > 1) {
    isMatch = county?.toLowerCase() === countyName?.toLowerCase();
  }

  return {
    isMatch: geoCountyArray.length > 1 ? countySelected && isMatch : isMatch,
    data: {
      city: _.startCase(_.toLower(geoCity)),
      state: stateName,
      zipcode: geoZipcode,
      county: countyName ? _.startCase(_.toLower(countyName)) : '',
      countyFips,
      region,
    },
    counties: geoCountyArray,
  };
};

export const mapMemberAddressToEdePayload = ({
  edeMembers,
  homeAddress,
  liveOutsideStateTemporarilyIndicator,
  mailingAddress,
  mappedType,
  transientAddress,
  noHomeAddressIndicator = false,
}: {
  edeMembers: Array<EdeMemberType>,
  homeAddress: FieldMapType,
  liveOutsideStateTemporarilyIndicator?: boolean,
  mailingAddress?: FieldMapType,
  mappedType: MapTypes,
  transientAddress?: FieldMapType,
  noHomeAddressIndicator?: boolean | null,
}): Array<Object> =>
  _.map(edeMembers, (edeMember) => {
    const homeAddressPayload = mapFieldsToAddress(homeAddress, mappedType, HOME_FIELD_MAP);
    const payload = {
      memberIdentifier: edeMember.memberIdentifier,
      edePayload: {
        demographic: {
          homeAddress: homeAddressPayload,
        },
      },
    };

    if (mailingAddress && !_.isEmpty(mailingAddress)) {
      _.set(
        payload,
        'edePayload.demographic.mailingAddress',
        mapFieldsToAddress(mailingAddress, mappedType, HOME_FIELD_MAP),
      );
    }

    if (transientAddress) {
      if (!_.isEmpty(transientAddress)) {
        _.set(
          payload,
          'edePayload.demographic.transientAddress',
          mapFieldsToAddress(transientAddress, mappedType, HOME_FIELD_MAP),
        );
        _.set(payload, 'edePayload.demographic.noHomeAddressIndicator', false);
      } else {
        _.set(payload, 'edePayload.demographic.transientAddress', null);
      }
    }
    if (typeof liveOutsideStateTemporarilyIndicator === 'boolean') {
      _.set(
        payload,
        'edePayload.demographic.liveOutsideStateTemporarilyIndicator',
        liveOutsideStateTemporarilyIndicator,
      );
    }

    if (noHomeAddressIndicator) {
      _.set(payload, 'edePayload.demographic.homeAddress', null);
    }
    _.set(payload, 'edePayload.demographic.noHomeAddressIndicator', noHomeAddressIndicator);

    return payload;
  });

const getPersonAddressByType = (person, type) =>
  person.addresses.find(({ addressType }) => addressType === type);

export const getPersonAddressWithRegion = person =>
  _.find(
    ADDRESS_TYPE_PRIORITY.map(type => getPersonAddressByType(person, type)),
    address => address?.region,
  );

export const getPersonBestAddress = (person) => {
  const transient = getPersonAddressByType(person, 'TRANSIENT');
  if (transient) {
    return transient;
  }
  const home = getPersonAddressByType(person, 'HOME');

  if (home) {
    return home;
  }

  const mailing = getPersonAddressByType(person, 'MAILING');

  if (mailing) {
    return mailing;
  }

  return null;
};

// A valid address is one that has both streetName and city values.
export const getPersonValidAddress = (person) => {
  const addressTypes = ['TRANSIENT', 'HOME', 'MAILING'];
  let validAddress;
  addressTypes.find((addressType) => {
    const address = getPersonAddressByType(person, addressType);
    if (_.get(address, 'streetAddress') && _.get(address, 'city')) {
      validAddress = address;
      return true;
    }
    return false;
  });
  return validAddress || {};
};

export const getPrimaryAddressWithRegion = (edeApp, person) => {
  const edeMember = getPrimaryMember(edeApp);
  const { homeAddress, mailingAddress, transientAddress } = _.get(edeMember, 'attestations.demographic', {});

  if (homeAddress) {
    const home = getPersonAddressByType(person, 'HOME');

    return {
      ...homeAddress,
      region: home && home.region,
    };
  }

  if (mailingAddress) {
    const home = getPersonAddressByType(person, 'MAILING');

    return {
      ...mailingAddress,
      region: home && home.region,
    };
  }

  if (transientAddress) {
    const home = getPersonAddressByType(person, 'TRANSIENT');

    return {
      ...transientAddress,
      region: home && home.region,
    };
  }

  return null;
};

const formatAddressPayload = ({ profileAddresses, addressData, addressType, mappedType }) => {
  const addressPayload = mapFieldsToAddress(addressData, mappedType, PROFILED_ADDRESS_FIELD_MAP);

  const matchedProfileAddress = _.find(profileAddresses, { addressType });
  const matchedId = _.get(matchedProfileAddress, 'id');
  if (matchedId) addressPayload.id = matchedId;
  addressPayload.addressType = addressType;
  return addressPayload;
};

export const mapMemberAddressToProfilePayload = ({
  homeAddress,
  mailingAddress,
  mappedType,
  transientAddress,
  profileAddresses,
}: {
  homeAddress: FieldMapType,
  liveOutsideStateTemporarilyIndicator?: boolean,
  mailingAddress?: FieldMapType,
  mappedType: MapTypes,
  transientAddress?: FieldMapType,
  profileAddresses?: Array<Object>,
}) => {
  const addressPayloads = [];

  if (!_.isEmpty(homeAddress)) {
    const homeAddressPayload = formatAddressPayload({
      profileAddresses,
      addressData: homeAddress,
      addressType: ADDRESS_TYPE.HOME,
      mappedType,
    });
    addressPayloads.push(homeAddressPayload);
  }

  if (!_.isEmpty(mailingAddress)) {
    const mailAddressPayload = formatAddressPayload({
      profileAddresses,
      addressData: mailingAddress,
      addressType: ADDRESS_TYPE.MAILING,
      mappedType,
    });
    addressPayloads.push(mailAddressPayload);
  }

  if (!_.isEmpty(transientAddress)) {
    const transientPayload = formatAddressPayload({
      profileAddresses,
      addressData: transientAddress,
      addressType: ADDRESS_TYPE.TRANSIENT,
      mappedType,
    });
    addressPayloads.push(transientPayload);
  }

  return addressPayloads;
};

export const isSameAddress = (address1: AddressFieldsType, address2: AddressFieldsType) =>
  _.reduce(
    _.keys(address1),
    (result, key) => {
      if (key === 'countyFips') return result;
      if (address1[key].toLowerCase() === address2[key].toLowerCase()) return result;
      return false;
    },
    true,
  );

export const getStateName = (stateCode) => {
  const states = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
  };

  return states[stateCode];
};
